import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  EventBusy as EventBusyIcon,
  ListAlt as ListAltIcon,
} from '@mui/icons-material';
import theme from '../../../../theme';
import {
  useCreateEmployeeUnavailabilityMutation,
  useDeleteEmployeeUnavailabilityMutation,
  useEmployeeUnavailabilitiesQuery,
  useResolveUnavailabilityImpactsMutation,
  useUnavailabilityImpactsQuery,
  useUpdateEmployeeUnavailabilityMutation,
} from '../../../../services/unavailabilityApi';
import EmployeeUnavailabilityForm from './EmployeeUnavailabilityForm';
import ImpactedAppointmentsTable from './ImpactedAppointmentsTable';

const formatPeriod = (item) => {
  const format = new Intl.DateTimeFormat('fr-FR', {
    dateStyle: 'medium',
    ...(!item.is_all_day ? { timeStyle: 'short' } : {}),
  });
  const start = format.format(new Date(item.starts_at));
  const rawEnd = new Date(item.ends_at);
  if (item.is_all_day) rawEnd.setDate(rawEnd.getDate() - 1);
  const end = format.format(rawEnd);
  return start === end ? start : `${start} → ${end}`;
};

const EmployeeUnavailabilityPanel = ({
  salonId,
  members = [],
  preselectedMember = null,
}) => {
  const [formOpen, setFormOpen] = useState(Boolean(preselectedMember));
  const [editing, setEditing] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const listQuery = useEmployeeUnavailabilitiesQuery(
    { salon: salonId, status: 'active' },
    { skip: !salonId },
  );
  const impactsQuery = useUnavailabilityImpactsQuery(selectedId, {
    skip: !selectedId,
  });
  const [createItem, createState] = useCreateEmployeeUnavailabilityMutation();
  const [updateItem, updateState] = useUpdateEmployeeUnavailabilityMutation();
  const [deleteItem] = useDeleteEmployeeUnavailabilityMutation();
  const [resolveImpacts, resolveState] = useResolveUnavailabilityImpactsMutation();

  useEffect(() => {
    if (preselectedMember) {
      setEditing(null);
      setFormOpen(true);
    }
  }, [preselectedMember]);

  const submit = async (payload) => {
    const result = editing
      ? await updateItem({ id: editing.unavailability_id, ...payload }).unwrap()
      : await createItem(payload).unwrap();
    setFormOpen(false);
    setEditing(null);
    setSelectedId(result.unavailability_id);
    setFeedback({ severity: 'success', text: 'Indisponibilité enregistrée.' });
  };

  const remove = async (item) => {
    if (!window.confirm('Supprimer cette indisponibilité ?')) return;
    try {
      await deleteItem(item.unavailability_id).unwrap();
      if (selectedId === item.unavailability_id) setSelectedId(null);
      setFeedback({ severity: 'success', text: 'Indisponibilité supprimée.' });
    } catch (_error) {
      setFeedback({ severity: 'error', text: 'Suppression impossible.' });
    }
  };

  const resolve = async (body) => {
    const result = await resolveImpacts({ id: selectedId, ...body }).unwrap();
    await impactsQuery.refetch?.();
    await listQuery.refetch?.();
    setFeedback({ severity: 'success', text: 'Rendez-vous traités avec succès.' });
    return result;
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            sx={{ fontWeight: 800, color: theme.colors.primary[700] }}
          >
            Congés et absences
          </Typography>
          <Typography color="text.secondary">
            Bloquez les créneaux et traitez les rendez-vous concernés.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setEditing(null);
            setFormOpen(true);
          }}
          sx={{ borderRadius: 2, backgroundColor: theme.colors.primary[600] }}
        >
          Ajouter une indisponibilité
        </Button>
      </Box>

      {feedback && (
        <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ mb: 2 }}>
          {feedback.text}
        </Alert>
      )}
      {listQuery.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Impossible de charger les congés et absences.
        </Alert>
      )}
      {listQuery.isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
          <CircularProgress />
        </Box>
      ) : !(listQuery.data || []).length ? (
        <Alert severity="info">Aucune indisponibilité active.</Alert>
      ) : (
        <Stack spacing={1.5}>
          {(listQuery.data || []).map((item) => (
            <Card
              key={item.unavailability_id}
              variant="outlined"
              sx={{
                borderRadius: 3,
                borderColor:
                  selectedId === item.unavailability_id
                    ? theme.colors.primary[400]
                    : theme.colors.grey[200],
              }}
            >
              <CardContent
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: 2,
                  '&:last-child': { pb: 2 },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Box
                    sx={{
                      width: 44,
                      height: 44,
                      borderRadius: 2,
                      display: 'grid',
                      placeItems: 'center',
                      color: theme.colors.primary[700],
                      backgroundColor: theme.colors.primary[50],
                    }}
                  >
                    <EventBusyIcon />
                  </Box>
                  <Box>
                    <Typography sx={{ fontWeight: 800 }}>{item.employee_name}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPeriod(item)}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Chip size="small" label={item.kind_label} />
                      {item.unresolved_count > 0 && (
                        <Chip
                          size="small"
                          color="warning"
                          label={`${item.unresolved_count} à traiter`}
                        />
                      )}
                    </Stack>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title="Voir les rendez-vous à traiter">
                    <IconButton onClick={() => setSelectedId(item.unavailability_id)}>
                      <ListAltIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton
                      onClick={() => {
                        setEditing(item);
                        setFormOpen(true);
                      }}
                    >
                      <EditIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Supprimer">
                    <IconButton color="error" onClick={() => remove(item)}>
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {selectedId && (
        <ImpactedAppointmentsTable
          unavailabilityId={selectedId}
          impacts={impactsQuery.data || []}
          onResolve={resolve}
          resolving={resolveState.isLoading}
        />
      )}

      <EmployeeUnavailabilityForm
        open={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSubmit={submit}
        members={members}
        preselectedMember={editing ? null : preselectedMember}
        initialValue={editing}
        isLoading={createState.isLoading || updateState.isLoading}
        salonId={salonId}
      />
    </Box>
  );
};

export default EmployeeUnavailabilityPanel;
